import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { TemplateFeatureAdminService } from "src/api"
import { Channel } from "src/channel"
import { shouldPublishSegments } from "src/helpers/shouldPublishSegments"
import { FormFields } from "src/lib/form-fields"
import { reportSuccess, reportError } from "src/lib/report"
import { ITemplateFeatureFormFields } from "src/modals/template-features/types/template-feature-form-fields"
import { EModalMode } from "src/types/modal/modal"

export class TemplateFeatureModalStore {
    //#region initialization
    static Context = React.createContext<TemplateFeatureModalStore | null>(null)

    formFields = new FormFields<ITemplateFeatureFormFields>({
        name: "",
        description: "",
        segment_ids: [],
        access_type: "NONE",
        dimensionIds: [],
    })

    private _isLoading = false
    private _id: number | undefined = undefined
    private _dimensionIds: number[] = []
    //#endregion

    //#region getters
    get isLoading() {
        return this._isLoading
    }

    private get id() {
        return this._id ?? undefined
    }

    get isEditMode() {
        return this.id !== undefined
    }

    get isReadOnly() {
        return this.formFields.get("access_type") === "READ"
    }

    get dimensionIDs() {
        return this._dimensionIds
    }
    //#endregion

    //#region setters
    setIsLoading = (loading: boolean) => {
        this._isLoading = loading
    }

    setId = (id: number) => {
        this._id = id
    }

    setDimensionIDs = (dimensionIDs: number[]) => {
        this._dimensionIds = dimensionIDs
        this.formFields.set("dimensionIds", dimensionIDs)
    }
    //#endregion

    constructor() {
        makeAutoObservable(this)
    }

    //#region store operations
    init = async (id?: number, accessGroupId?: number, mode?: EModalMode) => {
        //create
        if (id === undefined) {
            this.formFields.init({
                access_group_id: accessGroupId ?? 0,
                dimensionIds: [],
            })
            this._dimensionIds = []
            return
        }

        //update
        try {
            const response =
                await TemplateFeatureAdminService.getV1AdminTemplatefeature({
                    id,
                })

            this.formFields.init({
                access_group_id: response.access_group_id,
                name: response.name ?? "",
                description: response.description ?? "",
                segment_ids: response.segment_ids ?? [],
                dimensionIds: [], // keep for UI, not from API
            })
            this._dimensionIds = [] // keep for UI, not from API

            if (mode === EModalMode.Copy) {
                this.formFields.set("segment_ids", [])
                this.formFields.set("access_group_id", accessGroupId)
                this.formFields.set("dimensionIds", [])
                this._dimensionIds = []
            } else {
                this.setId(id)
            }
        } catch (error) {
            reportError(t`template-features.get-fail`, error)
        }
    }

    handleSubmit = async () => {
        if (!this.validateForm()) return

        this.id === undefined ? await this.create() : await this.update(this.id)
    }

    private validateForm = () => {
        this.formFields.clearErrors()
        this.formFields.validateRequiredFields([
            { field: "name" },
            { field: "description" },
            { field: "access_group_id" },
        ])

        return !this.formFields.hasErrors()
    }

    private create = async () => {
        try {
            const segmentIds = this.formFields.get("segment_ids")
            this.setIsLoading(true)

            const response =
                await TemplateFeatureAdminService.postV1AdminTemplatefeature({
                    request: {
                        name: this.formFields.get("name") ?? "",
                        description: this.formFields.get("description") ?? "",
                        access_group_id:
                            this.formFields.get("access_group_id") ?? 0,
                        // Add other fields as needed
                    },
                })

            if (Boolean(response.id) && shouldPublishSegments(segmentIds)) {
                await this.publish(response.id)
            }

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "template-feature",
                    action: "create",
                },
            })
            reportSuccess(t`template-features.create-success`)
        } catch (error) {
            reportError(t`template-features.create-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    private update = async (id: number) => {
        try {
            this.setIsLoading(true)

            await TemplateFeatureAdminService.putV1AdminTemplatefeature({
                id,
                request: {
                    name: this.formFields.get("name") ?? "",
                    description: this.formFields.get("description") ?? "",
                },
            })

            await this.publish(this.id)

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "template-feature",
                    action: "update",
                },
            })

            reportSuccess(t`template-features.update-success`)
        } catch (error) {
            reportError(t`template-features.update-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    private publish = async (id?: number) => {
        const segmentIds = this.formFields.get("segment_ids")
        // const dimensionIds = this._dimensionIds // for future API support

        if (id !== undefined && shouldPublishSegments(segmentIds)) {
            try {
                await TemplateFeatureAdminService.putV1AdminTemplatefeaturePublish(
                    {
                        id: id,
                        request: {
                            // TODO: Change published_in to segment_ids after API is updated
                            published_in: segmentIds,
                            // dimension_ids: dimensionIds, // for future API support
                        },
                    },
                )
            } catch (error) {
                reportError(t`global.publish-fail`, error)
            }
        }
    }
    //#endregion
}
